import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { getCriticalElementRevisions } from 'api/generated/critical-element-revisions/critical-element-revisions';

import Loader from 'components/partials/loader/loader';
import { formatDate } from 'helpers/dates-formatter';
import { renderCriticalElement } from 'containers/admin/clients/client/tabs-components/critical-elements/critical-elements-manager/critical-element/critical-element-helpers/critical-element-renderer';

import type { CriticalElement as CriticalElementType } from 'containers/admin/clients/client/tabs-components/goals-tab/types';

const ActionTypes: Record<string, string> = {
  INSERT: 'Critical Element Created',
  UPDATE: 'Critical Element Updated',
};

interface HistoryItemMetadata {
  createdBy: string;
  createdDate: string;
  type: 'INSERT' | 'UPDATE' | string;
}

interface HistoryItem {
  metadata: HistoryItemMetadata;
  revision: Partial<CriticalElementType>;
}

interface HistoryProps {
  data: { id: string | number | undefined } | CriticalElementType | null;
  userId: string | undefined;
  clientId: string | number | undefined;
}

const History = ({ data, userId, clientId }: HistoryProps): React.JSX.Element => {
  const id = (data as { id?: string | number } | null)?.id;
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [visibleItems, setVisibleItems] = useState<number>(0);

  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];

    const fetchCriticalElementHistory = async (): Promise<void> => {
      if (!userId || !clientId || !id) {
        setLoading(false);

        return;
      }

      try {
        const historyData = await getCriticalElementRevisions(
          userId,
          Number(clientId),
          Number(id),
        );

        setHistory(
          // TODO(ts-migration): OpenAPI revision DTO vs HistoryItem — migration-debt.md (2026-05-07 CE migration)
          historyData as unknown as HistoryItem[],
        );

        historyData.forEach((_, index) => {
          const timer = setTimeout(() => {
            setVisibleItems((prev) => prev + 1);
          }, index * 300);

          timers.push(timer);
        });
      } catch (error) {
        console.error('Error while fetching critical element history', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCriticalElementHistory();

    return () => {
      timers.forEach(clearTimeout);
    };
  }, [userId, clientId, id]);

  const renderRevisionMetadata = (
    createdBy: string,
    createdDate: string
  ): React.JSX.Element => (
    <div className="text-navy-500 text-xs">
      <p>{createdDate}</p>
      <p>{createdBy}</p>
    </div>
  );

  const renderAction = (type: string): React.JSX.Element => (
    <p className="font-bold text-navy-800">{ActionTypes[type]}</p>
  );

  const renderCriticalElementVersion = (
    type: 'Initial' | 'New' | 'Prior',
    item: Partial<CriticalElementType>
  ): React.JSX.Element => (
    <div className="flex gap-1 items-baseline text-gray-600 flex-col gap-0">
      <p className="text-gray-700 shrink-0 font-sm">{`${type} Version:`}</p>
      {renderCriticalElement(item as never)}
    </div>
  );

  const renderHistoryItem = (
    item: HistoryItem,
    index: number
  ): React.JSX.Element => {
    const { createdBy, createdDate, type } = item.metadata;
    const formattedDate = formatDate(createdDate, 'MMM D, YY h:mm A') ?? '';

    return (
      <>
        {renderAction(type)}
        {renderRevisionMetadata(createdBy, formattedDate)}
        <div className="mt-2 flex font-semibold gap-1 flex-col">
          {type === 'UPDATE' &&
            history[index + 1]?.revision &&
            renderCriticalElementVersion('Prior', history[index + 1]?.revision ?? '')}
          {renderCriticalElementVersion(
            type === 'INSERT' ? 'Initial' : 'New',
            item.revision
          )}
        </div>
      </>
    );
  };

  if (loading) {
    return (
      <Loader
        loading
        overlay="white"
        customClass="opacity-75"
        fullScreen={false}
      />
    );
  }

  return (
    <div className="relative flex flex-col items-start h-full">
      {history.map((item, index) => (
        <div
          key={item.metadata.createdDate}
          className={classNames(
            'relative flex items-baseline space-x-4 transition-opacity duration-500 ease-out transform-gpu',
            {
              'opacity-100 translate-y-0': visibleItems > index,
              'opacity-0 translate-y-4': visibleItems <= index,
            }
          )}
          style={{ transitionDelay: `${index * 300}ms` }}
        >
          {history.length > 1 && (
            <div className="flex flex-col items-center h-full">
              <div className="w-3 h-3 bg-navy-800 border-[3px] border-navy-200 rounded-full shrink-0"></div>
              {index < history.length - 1 && (
                <div
                  className={classNames(
                    'w-0.5 bg-navy-200 transition-all duration-500 ease-out',
                    {
                      'h-full': visibleItems > index,
                      'h-0': visibleItems <= index,
                    }
                  )}
                  style={{
                    transitionDelay: `${index * 300 + 200}ms`,
                  }}
                ></div>
              )}
            </div>
          )}
          <div className="w-full mb-2.5">{renderHistoryItem(item, index)}</div>
        </div>
      ))}
    </div>
  );
};

export default History;
